import React, { useState, useEffect } from 'react';
import { LogIn, Mail, Lock, Loader2, AlertCircle, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface LoginPageProps {
  onLoginSuccess: (token: string) => void;
}

export function LoginPage({ onLoginSuccess }: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [touched, setTouched] = useState({ email: false, password: false });

  useEffect(() => {
    if (touched.email && email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailError(emailRegex.test(email) ? '' : 'Please enter a valid email address');
    } else { setEmailError(''); }
  }, [email, touched.email]);

  useEffect(() => {
    if (touched.password && password) {
      setPasswordError(password.length < 6 ? 'Password must be at least 6 characters' : '');
    } else { setPasswordError(''); }
  }, [password, touched.password]);

  useEffect(() => { if (error) setError(''); }, [email, password]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    setError('');

    if (!email) { setEmailError('Email is required'); return; }
    if (!password) { setPasswordError('Password is required'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { setEmailError('Please enter a valid email address'); return; }
    if (password.length < 6) { setPasswordError('Password must be at least 6 characters'); return; }

    setLoading(true);
    setEmailError('');
    setPasswordError('');

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (authError) {
        let msg = authError.message || 'Login failed';
        if (msg.includes('Invalid login credentials')) msg = 'Invalid email or password. Please check your credentials.';
        else if (msg.includes('Email not confirmed')) msg = 'Please verify your email address before signing in.';
        else if (msg.includes('Too many requests')) msg = 'Too many login attempts. Please wait a moment.';
        setError(msg);
        setLoading(false);
        return;
      }

      if (data.session?.access_token) {
        localStorage.setItem('sb-auth-token', JSON.stringify({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        }));
        onLoginSuccess(data.session.access_token);
      } else {
        setError('No session token received. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-amber-600/6 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s' }} />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '7s', animationDelay: '2s' }} />
        <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-amber-600/3 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '9s', animationDelay: '1s' }} />
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: 'linear-gradient(rgba(217,119,6,1) 1px, transparent 1px), linear-gradient(90deg, rgba(217,119,6,1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="flex justify-center mb-10 animate-in fade-in duration-700">
          <img
            src="/logowhite.png"
            alt="SwadSeva OS"
            className="h-24 w-auto drop-shadow-2xl transition-transform hover:scale-105 duration-300"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </div>

        {/* Card */}
        <div className="bg-zinc-800/80 backdrop-blur-xl rounded-2xl border border-white/5 shadow-2xl shadow-black/60 p-7 sm:p-8 animate-in zoom-in-95 duration-500">
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-serif text-amber-50 mb-2">Super Admin</h1>
            <p className="text-zinc-400 text-sm mb-4">Sign in to manage all restaurants</p>
            {/* Security badge */}
            <div className="inline-flex items-center gap-1.5 bg-green-500/10 border border-green-500/20 rounded-full px-3 py-1">
              <ShieldCheck className="w-3.5 h-3.5 text-green-400" />
              <span className="text-xs text-green-400 font-medium">Secured & Encrypted</span>
            </div>
          </div>

          {error && (
            <div className="mb-5 p-4 bg-red-500/10 border border-red-500/20 rounded-xl animate-in slide-in-from-top duration-300 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm leading-relaxed">{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5" noValidate>
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-zinc-300 mb-1.5">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setTouched(prev => ({ ...prev, email: true }))}
                  required
                  autoComplete="email"
                  disabled={loading}
                  className={`w-full pl-10 pr-4 py-3 bg-zinc-900/60 border rounded-xl text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm ${emailError ? 'border-red-500/50 ring-red-500/20' :
                      (!emailError && touched.email && email) ? 'border-green-500/30' :
                        'border-zinc-700/60 focus:border-amber-500/50 focus:ring-amber-500/20'
                    }`}
                  placeholder="super@swadseva.com"
                />
              </div>
              {emailError && (
                <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1 animate-in">
                  <AlertCircle className="w-3.5 h-3.5" />{emailError}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-zinc-300 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => setTouched(prev => ({ ...prev, password: true }))}
                  required
                  autoComplete="current-password"
                  disabled={loading}
                  className={`w-full pl-10 pr-11 py-3 bg-zinc-900/60 border rounded-xl text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm ${passwordError ? 'border-red-500/50 ring-red-500/20' :
                      (!passwordError && touched.password && password) ? 'border-green-500/30' :
                        'border-zinc-700/60 focus:border-amber-500/50 focus:ring-amber-500/20'
                    }`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors focus:outline-none p-0.5"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {passwordError && (
                <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1 animate-in">
                  <AlertCircle className="w-3.5 h-3.5" />{passwordError}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !!emailError || !!passwordError}
              className="w-full bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-600 disabled:from-amber-600/50 disabled:to-amber-500/50 text-white py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-amber-600/25 hover:shadow-amber-600/40 hover:scale-[1.02] active:scale-[0.98] disabled:cursor-not-allowed disabled:scale-100 mt-2"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Signing in...</>
              ) : (
                <><LogIn className="w-4 h-4" />Sign In</>
              )}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-zinc-700/50 text-center">
            <p className="text-xs text-zinc-600">
              Forgot your password? Contact your administrator
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
